import { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from './hooks/redux';
import { getCurrentUser } from './store/slices/authSlice';
import { fetchDashboardData } from './store/slices/dashboardSlice';
import { fetchNotifications } from './store/slices/notificationsSlice';
import { fetchStores } from './store/slices/storesSlice';

// Components
import Layout from './components/Layout/Layout';
import Login from './components/Auth/Login';
import Signup from './components/Auth/Signup';
import ForgotPassword from './components/Auth/ForgotPassword';
import ResetPassword from './components/Auth/ResetPassword';

// Pages
import Dashboard from './pages/Dashboard';
import Stores from './pages/Stores';
import CreateStore from './pages/CreateStore';
import EditStore from './pages/EditStore';
import ViewStore from './pages/ViewStore';
import Listings from './pages/Listings';
import CreateListing from './pages/CreateListing';
import EditListing from './pages/EditListing';
import Leads from './pages/Leads';
import LeadDetails from './pages/LeadDetails';
import Reviews from './pages/Reviews';
import Analytics from './pages/Analytics';
import Notifications from './pages/Notifications';
import Profile from './pages/Profile';
import Settings from './pages/Settings';

function App() {
  const dispatch = useAppDispatch();
  const { isAuthenticated, loading, user } = useAppSelector((state) => state.auth);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token && !user && !loading) {
      dispatch(getCurrentUser());
    }
  }, [dispatch, user, loading]);

  useEffect(() => {
    if (isAuthenticated && user?.role === 'vendor') {
      // Fetch initial data when user is authenticated
      dispatch(fetchDashboardData());
      dispatch(fetchNotifications({}));
      dispatch(fetchStores({}));
    }
  }, [dispatch, isAuthenticated, user]);

  if (loading) {
    return (
      <div className="loading-container">
        <div className="text-center">
          <div className="inline-flex items-center justify-center mb-6">
            <img 
              src="/logo.png" 
              alt="The Old Cart" 
              className="h-20 object-contain animate-pulse"
            />
          </div>
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary-200 border-t-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  // Redirect non-vendors
  if (user?.role !== 'vendor') {
    return (
      <div className="loading-container">
        <div className="text-center max-w-md mx-auto p-8">
          <div className="inline-flex items-center justify-center mb-6">
            <img 
              src="/logo.png" 
              alt="The Old Cart" 
              className="h-20 object-contain"
            />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Access Denied</h1>
          <p className="text-gray-600 text-lg mb-6">This dashboard is only available for vendors.</p>
          <button
            onClick={() => {
              localStorage.removeItem('token');
              window.location.href = '/login';
            }}
            className="px-6 py-3 bg-primary-600 text-white rounded-lg font-semibold hover:bg-primary-700 transition-colors"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <Layout>
      <Routes>
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="/dashboard" element={<Dashboard />} />
        
        {/* Store Management */}
        <Route path="/stores" element={<Stores />} />
        <Route path="/stores/create" element={<CreateStore />} />
        <Route path="/stores/:id" element={<ViewStore />} />
        <Route path="/stores/:id/edit" element={<EditStore />} />
        
        {/* Listing Management */}
        <Route path="/listings" element={<Listings />} />
        <Route path="/listings/create" element={<CreateListing />} />
        <Route path="/listings/:id/edit" element={<EditListing />} />
        
        {/* Lead Management */}
        <Route path="/leads" element={<Leads />} />
        <Route path="/leads/:id" element={<LeadDetails />} />
        
        {/* Reviews */}
        <Route path="/reviews" element={<Reviews />} />
        
        {/* Analytics */}
        <Route path="/analytics" element={<Analytics />} />
        
        {/* Notifications */}
        <Route path="/notifications" element={<Notifications />} />
        
        {/* Profile & Settings */}
        <Route path="/profile" element={<Profile />} />
        <Route path="/settings" element={<Settings />} />
        
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </Layout>
  );
}

export default App;